
import { defineComponent, ref, toRefs, computed } from 'vue'
import { useInfiniteQuery } from '@tanstack/vue-query'

import type { Maybe, Status, RoomId } from '@/types'
import { SortDirection } from '@/services/BaseService.types'
import WeveIcon from '@/components/ui/icons/WeveIcon'
import {
  useDebouncedRef,
  useRouter,
  useRoomService,
  useRoomCRUD,
  useDefaultTags,
} from '@/use'

import { SidebarEntity } from './Sidebar.types'
import BaseSidebarSection from './BaseSidebarSection.vue'
import SidebarList from './SidebarList.vue'
import SidebarListItem from './SidebarListItem.vue'
import SidebarTime from './SidebarTime.vue'

const fields = {
  id: true,
  name: true,
  updatedAt: true,
} as const

export default defineComponent({
  name: 'RoomSidebarSection',
  components: {
    WeveIcon,
    BaseSidebarSection,
    SidebarList,
    SidebarListItem,
    SidebarTime,
  },
  props: {
    expanded: {
      type: Boolean,
      default: false,
    },
    open: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const { open } = toRefs(props)
    const router = useRouter()
    const { service } = useRoomService()
    const { create, del, copy } = useRoomCRUD()
    const query = ref('')

    const { tags } = useDefaultTags('room-search-tags')

    const status = ref<Maybe<Status>>(undefined)
    const debouncedQuery = useDebouncedRef(query)
    const limit = 25

    const key = ['rooms', debouncedQuery, status, tags, fields, limit]

    const { isLoading, isRefetching, data, refetch, fetchNextPage } =
      useInfiniteQuery({
        queryKey: key,
        queryFn: ({ pageParam }) =>
          service.search(
            { query: query.value, status: status.value, tags: tags.value },
            fields,
            {
              limit,
              page: pageParam,
            },
            [
              {
                field: 'updatedAt',
                direction: SortDirection.DESC,
              },
            ]
          ),
        getNextPageParam: (page) => {
          return page.page === page.pageCount ? undefined : page.page + 1
        },
        enabled: open,
        initialPageParam: 1,
      })
    const rooms = computed(() =>
      (data.value?.pages ?? []).map((page) => page.data).flat()
    )
    const loading = computed(() => isLoading.value || isRefetching.value)

    function toggle() {
      emit('toggle', SidebarEntity.ROOM)
    }

    async function createRoom() {
      const room = await create()
      if (room === null) return
      router.push({ name: 'room', params: { id: room.id.toString() } })
      await refetch()
    }

    async function copyRoom(id: RoomId, name: string) {
      const result = await copy(id, name)
      if (result === undefined) return
      router.push({ name: 'room', params: { id: result.toString() } })
      await refetch()
    }

    async function deleteRoom(id: RoomId) {
      const res = await del(id)
      if (res === null) {
        return
      }
      await refetch()
    }

    return {
      rooms,
      loading,
      fetchNextPage,
      toggle,
      createRoom,
      copyRoom,
      deleteRoom,
      query,
      tags,
      status,
      SidebarEntity,
    }
  },
})
